// Static registry mirroring the SEO each public page sets via useSeo.
// Used by the internal /admin/seo dashboard to audit pages at a glance.
import { THEME_PAGES, INFO_PAGES, GUIDES } from "./sitePages";
import { DESTINATIONS } from "./destinations";
import { escapeTitle, escapeDescription } from "../lib/seoText";

const SITE = "https://yogatoyacht.com";

const mon = (newsletter, affiliate, recommendation, provider) => ({ newsletter, affiliate, recommendation, provider });

export function buildSeoRegistry() {
  const rows = [];

  rows.push({
    path: "/", title: "YogaToYacht | Find Yoga, Wellness & Yacht Retreats",
    description: "Discover yoga retreats, wellness escapes, sailing retreats, and yacht experiences based on your destination, budget, and travel style.",
    h1: "Find your perfect yoga, wellness, or yacht retreat", indexable: true, inSitemap: true,
    monetization: mon(true, true, true, true), group: "Core",
  });

  rows.push({
    path: "/find-my-retreat", title: "Find My Retreat — Take the Quiz | YogaToYacht",
    description: "Answer five quick questions and we'll match you to the perfect yoga, wellness, yacht or beginner-friendly retreat in Europe.",
    h1: "Find My Retreat quiz", indexable: true, inSitemap: true,
    monetization: mon(true, true, true, true), group: "Core",
  });

  Object.values(THEME_PAGES).forEach((p) => rows.push({
    path: `/${p.slug}`, title: p.title, description: p.description, h1: p.h1,
    indexable: true, inSitemap: true, monetization: mon(true, true, true, true), group: "Theme landing",
  }));

  DESTINATIONS.filter((d) => !["all", "travel", "yoga", "wellness", "adventure"].includes(d.id)).forEach((d) => rows.push({
    path: `/escape/${d.id}`,
    title: escapeTitle(d),
    description: escapeDescription(d),
    h1: `${d.title}, ${d.country}`, indexable: true, inSitemap: true,
    monetization: mon(false, true, false, false), group: "Escape",
  }));

  Object.values(INFO_PAGES).forEach((p) => rows.push({
    path: `/${p.slug}`, title: p.title, description: p.description, h1: p.h1,
    indexable: true, inSitemap: true, monetization: mon(false, false, false, false), group: "Info / legal",
  }));

  rows.push({
    path: "/guides", title: "Guides (Coming Soon) | YogaToYacht",
    description: "In-depth YogaToYacht guides to choosing yoga, wellness, sailing and yacht retreats — coming soon.",
    h1: "Retreat guides", indexable: false, inSitemap: false,
    monetization: mon(true, false, false, false), group: "Guides",
  });

  GUIDES.forEach((g) => rows.push({
    path: `/guides/${g.slug}`, title: `${g.title} (Coming Soon) | YogaToYacht`,
    description: `${g.title} — an in-depth YogaToYacht guide, coming soon.`,
    h1: g.title, indexable: false, inSitemap: false,
    monetization: mon(true, false, false, false), group: "Guides",
  }));

  return rows.map((r) => ({ ...r, canonical: `${SITE}${r.path}` }));
}

export function auditRow(r) {
  const issues = [];
  const fixes = [];
  if (!r.title) { issues.push("Missing title"); fixes.push("Add a <title>"); }
  else if (r.title.length > 65) { issues.push(`Title long (${r.title.length} chars)`); fixes.push("Trim title to ≤ 60 chars"); }
  if (!r.description) { issues.push("Missing meta description"); fixes.push("Add a meta description"); }
  else if (r.description.length > 165) { issues.push(`Description long (${r.description.length})`); fixes.push("Trim description to ≤ 160 chars"); }
  else if (r.description.length < 50) { issues.push(`Description short (${r.description.length})`); fixes.push("Expand description to ≥ 50 chars"); }
  if (!r.h1) { issues.push("Missing H1"); fixes.push("Add a single H1"); }
  if (!r.canonical) { issues.push("Missing canonical"); fixes.push("Add canonical link"); }
  if (r.indexable && !r.inSitemap) { issues.push("Indexable but not in sitemap"); fixes.push("Add URL to sitemap.xml"); }
  if (!r.indexable && r.inSitemap) { issues.push("Noindex but listed in sitemap"); fixes.push("Remove URL from sitemap.xml"); }

  const status = !r.indexable ? "Noindex" : issues.length ? "Issues" : "Good";
  return { issues, fixes, status };
}
